import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { VendorSearchContents } from "../../components/VendorSearchContents";
import { usePrimaryOrganizationId } from "../../hooks";
import { useDataRecipientSearch, useOrganizationVendors } from "../../hooks/useOrganizationVendors";
import { RouteHelpers } from "../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";

export const DataRecipientNewPage = () => {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();

  // Local loading state to persist until navigation completes
  const [isPostAddProcessing, setIsPostAddProcessing] = useState(false);

  const [, , refreshOrgVendors] = useOrganizationVendors(orgId, undefined, true);

  // Single function to handle post-add processing for all add operations
  const handlePostAddSuccess = async (dr: OrganizationDataRecipientDto) => {
    setIsPostAddProcessing(true);

    try {
      await refreshOrgVendors();
      history.push(RouteHelpers.dataMap.dataRecipient.workflow.groupAssociation(dr.vendorId));
    } catch (error) {
      setIsPostAddProcessing(false);
    }
  };

  const { actions, requests, data } = useDataRecipientSearch(orgId, {
    onAddSuccess: handlePostAddSuccess,
    onAddCustomSuccess: handlePostAddSuccess,
    onAddContractorSuccess: handlePostAddSuccess,
    onAddThirdParyRecipientSuccess: handlePostAddSuccess,
    onAddStorageLocationSuccess: handlePostAddSuccess,
  });

  // Create a combined request state that includes post-add processing
  const combinedAddRequest = useMemo(() => {
    const isAnyAddRunning =
      requests.addingVendor.running ||
      requests.addingCustomVendor.running ||
      requests.addingThirdPartyRecipient.running ||
      requests.addingContractor.running ||
      requests.addingStorageLocation.running;

    const isAnyProcessingRunning = isAnyAddRunning || isPostAddProcessing;

    return {
      ...requests.addingVendor,
      running: isAnyProcessingRunning,
    };
  }, [
    requests.addingVendor,
    requests.addingCustomVendor,
    requests.addingThirdPartyRecipient,
    requests.addingContractor,
    requests.addingStorageLocation,
    isPostAddProcessing,
  ]);

  return (
    <StandardPageWrapper
      requests={[requests.catalogRequest, requests.orgDataRecipientsRequest]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              hideNext={true}
              prevContent="Back"
              prevDisabled={combinedAddRequest.running}
              prevOnClick={() => history.goBack()}
            />
          }
        />
      }
    >
      <PageHeader titleContent={"Add Data Recipient"} />

      <VendorSearchContents
        allVendorsMap={data.allVendorsMap}
        enabledVendors={data.enabledVendors}
        defaultVendors={data.catalog.slice(0, 8)}
        type="vendor"
        context={["CONSUMER"]}
        handleAddVendor={actions.handleAddVendor}
        handleAddCustomVendor={actions.handleAddCustomVendor}
        handleAddContractor={actions.handleAddContractor}
        handleAddThirdParyRecipient={actions.handleAddThirdParyRecipient}
        handleAddStorageLocation={actions.handleAddStorageLocation}
        addRequest={combinedAddRequest}
        showCustomVendorControls={true}
        variant="radio"
      />
    </StandardPageWrapper>
  );
};
